/**
 * <TutorLiveActionBar> — les gestes du centre du live, dans une barre flottante en bas au centre (molecule).
 *
 * Wajih, 04/11 : la tête du centre empilait trois lignes (l'élève, les gestes, les onglets). Les gestes
 * descendent ici, dans une pilule qui flotte au-dessus du contenu, comme la barre d'outils de Lovable ; en
 * haut ne restent que l'élève et les onglets.
 *
 * **Elle suit l'onglet ouvert** (`tutorLiveActionBarParts`, pur et testé) : sur une vue de l'activité, son
 * titre et son chrono, son statut, l'action qu'il attend, « Afficher à l'élève », « Présenter » et « Plus » ;
 * sur la visio ou les contrôles, « + Activité » seul. Le centre décide de ce qui s'y pose ; elle ne fait que le
 * montrer.
 *
 * **« + Activité » est toujours là, en tête** (Wajih, live du 04/11 au soir) : le geste le plus fréquent du live a
 * quitté la barre du bas pour celle-ci, sur chaque onglet. Il fait ressortir le champ de génération, en bas à droite,
 * pré-rempli sur l'activité ouverte. Un centre étroit prend les mots courts (`compact`), et la pilule passe à la ligne
 * plutôt que de couper un geste.
 *
 * **Toutes les actions en bas** (live du 04/11) : la barre du tableau blanc — l'encre, annuler, tout effacer,
 * « Générer un visuel », la bascule du visuel et son avis — y descend (`tools`, `<TutorAnnotationToolbar>`), en
 * tête de la pilule ; pendant une présentation, la barre de dessin et « Arrêter » y sont aussi (`tools` avec
 * `onStop`, ou `stop` seul sans dessin). Elle ne se pose jamais sur ce que voient les élèves : le centre lui
 * garde sa bande, sous la région partagée.
 *
 * **« + Activité » appelle quand il est seul** (Wajih, live du 04/11 : « plus animé ») : sur la visio, c'est le seul
 * geste de la barre ; un halo qui respire doucement l'entoure, pour qu'on le trouve. À côté des gestes d'une activité,
 * il garde l'aplat de l'univers, sans halo. En mouvement réduit, le halo reste posé, sans bouger.
 *
 * Contrôlée : chaque geste repart.
 */
import React, { useEffect, useMemo, useRef } from "react"
import { Animated, Easing, Platform, View } from "react-native"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { type UIActivityDurationTone, type UIActivityStatus } from "../../../ports/primary/ui/types/activity"
import { type UITutorActivityAction } from "../../../ports/primary/ui/types/tutorEleves"
import { Badge } from "../../atoms/Badge"
import { Button } from "../../atoms/Buttons/Button"
import { type IconName } from "../../atoms/icons/Icon/iconRegistry"
import { Text } from "../../atoms/typography/Text"
import { useReduceMotion } from "../../hooks/useReduceMotion"
import { motion } from "../../theme/tokens/motion"
import { injectStyleOnce } from "../../utils/injectStyleOnce"
import { shadowStyle } from "../../utils/shadow"
import { ActivityStatusBadge } from "../ActivityStatusBadge"
import { TutorAnnotationToolbar, type TutorAnnotationToolbarProps } from "../TutorAnnotationToolbar"
import {
    ACTIVITY_ACTIONS,
    ACTIVITY_SHOWN_LABEL,
    ACTIVITY_SHOWN_SHORT,
} from "../TutorLiveCenterHead/tutorActivityActions"

export type TutorLiveActionBarProps = {
    /** L'activité ouverte : son titre (absent quand l'encart de l'activité le dit déjà), et son chrono. */
    info?: { title?: string; chrono?: { label: string; tone?: UIActivityDurationTone } }
    status?: UIActivityStatus
    /** L'action qu'attend le statut (Valider, Redonner, Soumettre). */
    primary?: { action: UITutorActivityAction; onPress: () => void; pending?: boolean }
    /** « Afficher à l'élève » ; `shown` : il l'a devant lui. */
    show?: { onPress: () => void; pending?: boolean; shown?: boolean; pressableWhenShown?: boolean }
    /** « Présenter » (jamais « Arrêter » : pendant une présentation, la barre se retire). */
    present?: { onPress: () => void; pending?: boolean }
    /** « + Activité », en tête : le champ de génération ressort, pré-rempli. */
    onAdd?: () => void
    /** « Plus » : les autres gestes. */
    onOpenMenu?: () => void
    /** La barre de dessin : celle du tableau blanc, ou celle de la présentation (avec « Arrêter »). */
    tools?: TutorAnnotationToolbarProps
    /** « Arrêter » la présentation, quand elle n'a pas de barre de dessin. */
    stop?: { onPress: () => void; pending?: boolean }
    /** Un centre étroit : les mots courts, sans le titre de l'activité. Défaut `false`. */
    compact?: boolean
    testID?: string
}

const TEXTS = {
    bar: "Gestes sur l'activité",
    present: "Présenter",
    stop: "Arrêter",
    stopLabel: "Arrêter la présentation",
    add: "Activité",
    addLabel: "Ajouter une activité",
    more: "Plus d'actions sur l'activité",
    menu: "Plus",
}

/** Le chrono, au ton du temps prévu (comme `<TutorLiveCenterHead>`). */
const CHRONO_VARIANT = { on_time: "success", over: "warning" } as const
/** Le titre de l'activité cède sa place aux gestes. */
const INFO_MAX = 200

export function TutorLiveActionBar(props: TutorLiveActionBarProps) {
    const { info, status, primary, show, present, onAdd, onOpenMenu, tools, stop, compact = false, testID } = props
    const shown = show?.shown === true
    // Seul geste de la barre (la visio, les contrôles) : il appelle.
    const alone = [info, status, primary, show, present, tools, stop].every((part) => part == null)
    const add =
        onAdd != null ? (
            <Action
                label={TEXTS.addLabel}
                shortLabel={TEXTS.add}
                iconName="plus"
                solid
                pending={false}
                onPress={onAdd}
            />
        ) : null
    return (
        <View style={styles.pill} accessibilityRole="toolbar" accessibilityLabel={TEXTS.bar} testID={testID}>
            {add != null && (alone ? <Calling>{add}</Calling> : add)}
            {tools != null && <TutorAnnotationToolbar {...tools} />}
            {stop != null && (
                <Button
                    label={TEXTS.stop}
                    accessibilityLabel={TEXTS.stopLabel}
                    iconName="chalkboard-teacher"
                    color="danger"
                    size="sm"
                    loading={stop.pending === true}
                    onPress={stop.onPress}
                />
            )}
            {info != null && !compact && (
                <View style={styles.info}>
                    {info.title != null && (
                        <Text size="text-sm" weight="bold" color="text-strong" numberOfLines={1}>
                            {info.title}
                        </Text>
                    )}
                    {info.chrono != null && (
                        <Badge
                            text={info.chrono.label}
                            variant={info.chrono.tone != null ? CHRONO_VARIANT[info.chrono.tone] : "neutral"}
                            iconName="stopwatch"
                            tinted
                            truncate={false}
                        />
                    )}
                </View>
            )}
            {status != null && !compact && <ActivityStatusBadge status={status} />}
            {primary != null && (
                <Action
                    label={ACTIVITY_ACTIONS[primary.action].label}
                    iconName={ACTIVITY_ACTIONS[primary.action].icon}
                    solid
                    pending={primary.pending === true}
                    onPress={primary.onPress}
                />
            )}
            {show != null && (
                <Action
                    label={shown ? ACTIVITY_SHOWN_LABEL : ACTIVITY_ACTIONS.show.label}
                    shortLabel={compact ? (shown ? ACTIVITY_SHOWN_SHORT : ACTIVITY_ACTIONS.show.short) : undefined}
                    iconName={ACTIVITY_ACTIONS.show.icon}
                    done={shown}
                    pending={show.pending === true}
                    disabled={shown && show.pressableWhenShown !== true}
                    onPress={show.onPress}
                />
            )}
            {present != null && (
                <Action
                    label={TEXTS.present}
                    iconName="chalkboard-teacher"
                    pending={present.pending === true}
                    onPress={present.onPress}
                />
            )}
            {onOpenMenu != null && (
                <Button
                    label={TEXTS.menu}
                    iconName="ellipsis-h"
                    variant="ghost"
                    size="sm"
                    onPress={onOpenMenu}
                    accessibilityLabel={TEXTS.more}
                />
            )}
        </View>
    )
}

/** Un halo qui respire autour du geste qu'on doit trouver ; posé, sans bouger, en mouvement réduit. */
function Calling(props: { children: React.ReactNode }) {
    const { theme } = useUnistyles()
    const reduceMotion = useReduceMotion()
    const web = Platform.OS === "web"
    const breath = useRef(new Animated.Value(reduceMotion ? 0.5 : 0)).current
    const scale = useMemo(() => breath.interpolate({ inputRange: [0, 1], outputRange: [1, 1.12] }), [breath])
    useEffect(() => {
        breath.setValue(reduceMotion ? 0.5 : 0)
        if (reduceMotion) return undefined
        // Au web, le navigateur joue le halo seul (comme `<LiveDot>`, `<Blink>`) : une boucle d'`Animated` y coûterait
        // du script à chaque image, à côté de la vidéo du live.
        if (web) {
            injectStyleOnce(WEB_HALO, WEB_HALO_CSS)
            return undefined
        }
        const half = (toValue: number) =>
            Animated.timing(breath, {
                toValue,
                duration: theme.motion.entree * 2,
                easing: Easing.bezier(...theme.motion.easing),
                useNativeDriver: true,
            })
        const loop = Animated.loop(Animated.sequence([half(1), half(0)]))
        loop.start()
        return () => loop.stop()
    }, [breath, reduceMotion, web, theme.motion])
    return (
        <View style={styles.calling}>
            {web && !reduceMotion ? (
                <View pointerEvents="none" style={[HALO_FILL, styles.webHalo]}>
                    <View style={styles.halo} />
                </View>
            ) : (
                // L'opacité et l'échelle passent sur une vue nue : le style Unistyles reste sur la vue dedans.
                <Animated.View pointerEvents="none" style={[HALO_FILL, { opacity: breath, transform: [{ scale }] }]}>
                    <View style={styles.halo} />
                </Animated.View>
            )}
            {props.children}
        </View>
    )
}

const WEB_HALO = "masteur-calling-halo"
const WEB_HALO_CSS = `@keyframes ${WEB_HALO} {
  0%, 100% { opacity: 0; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.12); }
}`

const HALO_FILL = { position: "absolute" as const, top: -4, left: -4, right: -4, bottom: -4 }

/** Un geste de la barre : le secondaire du livre, le principal pour l'action du statut. */
function Action(props: {
    label: string
    shortLabel?: string
    iconName?: IconName
    solid?: boolean
    pending: boolean
    disabled?: boolean
    /** L'action est déjà faite : la forme « déjà fait » (`<Button done>`). */
    done?: boolean
    onPress: () => void
}) {
    const { label, shortLabel, iconName, solid = false, pending, disabled = false, done = false, onPress } = props
    return (
        <Button
            label={shortLabel ?? label}
            accessibilityLabel={label}
            iconName={iconName}
            variant={solid ? "solid" : "outline"}
            size="sm"
            loading={pending}
            disabled={disabled}
            done={done}
            onPress={onPress}
        />
    )
}

const styles = StyleSheet.create((theme) => ({
    calling: { position: "relative" as const },
    webHalo: {
        _web: {
            animationName: WEB_HALO,
            // Les nombres du jeton, pas ceux du thème : au web, Unistyles fait du thème des variables CSS.
            animationDuration: `${motion.entree * 4}ms`,
            animationIterationCount: "infinite",
            animationTimingFunction: `cubic-bezier(${motion.easing.join(", ")})`,
        },
    },
    halo: { flex: 1, borderRadius: theme.radius.full, backgroundColor: theme.colors["role-selected"], opacity: 0.35 },
    /** La pilule : elle flotte (l'ombre des surfaces qui flottent), et passe à la ligne plutôt que de couper. */
    pill: {
        flexDirection: "row" as const,
        flexWrap: "wrap" as const,
        alignItems: "center" as const,
        justifyContent: "center" as const,
        columnGap: theme.spacing["2"],
        rowGap: theme.spacing["2"],
        maxWidth: "100%",
        paddingVertical: theme.spacing["2"],
        paddingHorizontal: theme.spacing["3"],
        borderRadius: theme.radius.xl,
        backgroundColor: theme.colors["surface-floating"],
        borderWidth: 1,
        borderColor: theme.colors["border-subtle"],
        ...shadowStyle(theme, "floating"),
    },
    info: {
        flexDirection: "row" as const,
        alignItems: "center" as const,
        gap: theme.spacing["2"],
        flexShrink: 1,
        minWidth: 0,
        maxWidth: INFO_MAX,
        paddingRight: theme.spacing["1"],
    },
}))
